import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { v4 as uuidv4 } from 'uuid';
import logger from '@/services/logger';

export interface NotificationAction {
  label: string;
  onClick: () => void;
}

export interface NotificationState {
  id: string;
  type: 'info' | 'success' | 'warning' | 'error';
  title: string;
  message: string;
  autoDismiss: boolean;
  duration: number; // in milliseconds
  createdAt: number; // timestamp
  read: boolean;
  actions?: NotificationAction[];
}

export interface NotificationsState {
  notifications: NotificationState[];
  history: NotificationState[];
  config: {
    position: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left';
    maxVisible: number;
    defaultDuration: number;
    maxHistory: number;
    minTimeBetweenSimilar: number; // Minimum time between similar notifications in ms
  };
  lastNotificationTime: number; // Track the last time a notification was added
}

const initialState: NotificationsState = {
  notifications: [],
  history: [],
  config: {
    position: 'top-right',
    maxVisible: 3,
    defaultDuration: 5000, // 5 seconds
    maxHistory: 50,
    minTimeBetweenSimilar: 1000, // 1 second between similar notifications
  },
  lastNotificationTime: 0,
};

// Interface for adding a new notification
export interface AddNotificationPayload {
  type: 'info' | 'success' | 'warning' | 'error';
  title: string;
  message: string;
  autoDismiss?: boolean;
  duration?: number;
  actions?: NotificationAction[];
  // If true, will combine with similar notifications rather than creating duplicates
  preventDuplicates?: boolean;
}

// Interface for updating notification configuration
export interface UpdateConfigPayload {
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left';
  maxVisible?: number;
  defaultDuration?: number;
  maxHistory?: number;
  minTimeBetweenSimilar?: number;
}

const notificationsSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    addNotification: (state, action: PayloadAction<AddNotificationPayload>) => {
      const {
        type,
        title,
        message,
        autoDismiss = true,
        duration = state.config.defaultDuration,
        actions = [],
        preventDuplicates = false,
      } = action.payload;

      const now = Date.now();

      // Check for similar notifications if preventDuplicates is true
      if (preventDuplicates) {
        const similarNotificationExists = state.notifications.some(
          n =>
            n.title === title &&
            n.type === type &&
            now - n.createdAt < state.config.minTimeBetweenSimilar
        );

        if (similarNotificationExists) {
          logger.debug(`Similar notification suppressed: ${title}`, {
            module: 'notifications',
            data: { type, message },
          });
          return;
        }
      }

      const notification: NotificationState = {
        id: uuidv4(),
        type,
        title,
        message,
        autoDismiss,
        duration,
        createdAt: now,
        read: false,
        actions,
      };

      state.notifications.push(notification);
      state.lastNotificationTime = now;

      logger.info(`Notification added: ${title}`, {
        module: 'notifications',
        data: { type, message },
      });
    },

    removeNotification: (state, action: PayloadAction<string>) => {
      const notificationId = action.payload;
      const notificationIndex = state.notifications.findIndex(n => n.id === notificationId);

      if (notificationIndex !== -1) {
        const notification = state.notifications[notificationIndex];

        // Add to history if not already at max
        if (state.history.length < state.config.maxHistory) {
          state.history.unshift({ ...notification, read: true });
        } else {
          // Remove oldest and add new one
          state.history.pop();
          state.history.unshift({ ...notification, read: true });
        }

        // Remove from active notifications
        state.notifications.splice(notificationIndex, 1);

        logger.debug(`Notification removed: ${notification.title}`, {
          module: 'notifications',
          data: { id: notificationId },
        });
      }
    },

    clearAllNotifications: state => {
      // Move all unread notifications to history
      const unreadNotifications = state.notifications.map(n => ({ ...n, read: true }));

      // Add unread notifications to history
      const newHistory = [...unreadNotifications, ...state.history];

      // Ensure history doesn't exceed max length
      state.history = newHistory.slice(0, state.config.maxHistory);

      // Clear active notifications
      state.notifications = [];

      logger.info('All notifications cleared', { module: 'notifications' });
    },

    markAsRead: (state, action: PayloadAction<string>) => {
      const notificationId = action.payload;
      const notification = state.notifications.find(n => n.id === notificationId);

      if (notification) {
        notification.read = true;
        logger.debug(`Notification marked as read: ${notification.title}`, {
          module: 'notifications',
          data: { id: notificationId },
        });
      }
    },

    updateConfig: (state, action: PayloadAction<UpdateConfigPayload>) => {
      state.config = {
        ...state.config,
        ...action.payload,
      };

      logger.info('Notification configuration updated', {
        module: 'notifications',
        data: action.payload,
      });
    },

    clearHistory: state => {
      state.history = [];
      logger.info('Notification history cleared', { module: 'notifications' });
    },
  },
});

export const {
  addNotification,
  removeNotification,
  clearAllNotifications,
  markAsRead,
  updateConfig,
  clearHistory,
} = notificationsSlice.actions;

export default notificationsSlice.reducer;
